import { FC, FunctionComponent, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ITrial, ITrialFormMetadata, PostSaveSourceDataConfiguration } from '@/api/dtos';
import { Divider, List, LinearProgress, Box, Paper, Grid, Tabs, Tab, Fade, styled, alpha } from '@mui/material'
import { Fragment } from 'react';
import { Form, FormsFeatureContext, FormSubmitButton, InputOnlyField, IValidationError, Text } from '@ngt/forms';
import ConfigurationFormEventSkeleton from './ConfigurationFormEventSkeleton';
import ConfigurationFormEvent from './ConfigurationFormEvent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faSquareList } from '@fortawesome/duotone-light-svg-icons';
import ConfigurationFormEditContext, { IConfigurationFormEditContext } from '@/contexts/forms/ConfigurationFormEditContext';
import ConfigurationFormEdit from './edit/ConfigurationFormEdit';
import { IFormSubmit, Observable } from '@ngt/forms-core';
import { ISourceDataConfiguration, SourceDataConfigurationSingleResponse } from '../../../api/dtos';
import { useNavigate, useParams } from 'react-router-dom';
import SkeletonExt from '@/components/layouts/SkeletonExt';
import ConfigurationFormProcessor, { ConfigurationType, IConfigurationData, IConfigurationItem } from '@/utilities/forms/ConfigurationFormProcessor';
import { Virtuoso } from 'react-virtuoso';
import ConfigurationFormForm from './ConfigurationFormForm';
import ConfigurationFormVersion from './ConfigurationFormVersion';
import ConfigurationFormSection from './ConfigurationFormSection';
import ConfigurationFormField from './ConfigurationFormField';
import ConfigurationFormFieldValue from './ConfigurationFormFieldValue';


const GridSearchColumn = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        order: 2
    }
}));

const ConfigurationDetails = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));

const InputSkeleton = styled(SkeletonExt)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.13)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.13)}`
}));

// Item contents are cached properly with React.memo
const InnerItem = memo(({ index, item, context}: {index: number; item: IConfigurationItem; context: IVirtuosoContext}) => {
    if (item.type === ConfigurationType.Event) {
        return ( 
            <ConfigurationFormEvent 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.eventIdentifier!, value, collapseChildren)} 
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.eventIdentifier!, collapseChildren)} 
                expanded={context.expanded[item.eventIdentifier!]} 
            />
        )
    }
    
    if (item.type === ConfigurationType.Form) {
        return (
            <ConfigurationFormForm 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.formIdentifier!, value, collapseChildren)} 
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.formIdentifier!, collapseChildren)}  
                expanded={context.expanded[item.formIdentifier!]} 
            />
        )
    }

    if (item.type === ConfigurationType.Version) {
        return ( 
            <ConfigurationFormVersion 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.formVersionIdentifier!, value, collapseChildren)} 
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.formVersionIdentifier!, collapseChildren)}  
                expanded={context.expanded[item.formVersionIdentifier!]} 
            />
        )
    }

    if (item.type === ConfigurationType.Section) {
        return ( 
            <ConfigurationFormSection 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.formSectionIdentifier!, value, collapseChildren)} 
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.formSectionIdentifier!, collapseChildren)} 
                expanded={context.expanded[item.formSectionIdentifier!]} 
            />
        )
    }

    if (item.type === ConfigurationType.Field) {
        return ( 
            <ConfigurationFormField 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.formFieldIdentifier!, value, collapseChildren)}  
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.formFieldIdentifier!, collapseChildren)}  
                expanded={context.expanded[item.formFieldIdentifier!]} 
            />
        )
    }

    if (item.type === ConfigurationType.Value) {
        return ( 
            <ConfigurationFormFieldValue 
                {...item} 
                setExpanded={(value: boolean, collapseChildren?: boolean) => context.setExpanded(item.formFieldValueIdentifier!, value, collapseChildren)}  
                toggleExpand={(collapseChildren?: boolean) => context.toggleExpanding(item.formFieldValueIdentifier!, collapseChildren)}  
                expanded={context.expanded[item.formFieldValueIdentifier!]} 
            />
        )
    }

    return (
        <>
            Unknown Item
        </>
    )
  })
  
type FormType = { configuration?: ISourceDataConfiguration; trialConfiguration?: ISourceDataConfiguration };

interface IVirtuosoContext { 
    setExpanded: (identifier: string, value: boolean, collapseChildren?: boolean) => void;
    toggleExpanding: (identifier: string, collapseChildren?: boolean) => void;
    expanded: Record<string, boolean>
}

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: IConfigurationItem, context: IVirtuosoContext) => {
    return <InnerItem index={index} item={item} context={context} />
}

interface IConfigurationForm {
    isTrialConfiguration?: boolean;
    trialConfiguration?: ISourceDataConfiguration;
    configuration?: ISourceDataConfiguration;
    updateConfiguration?: (data?: SourceDataConfigurationSingleResponse, revalidate?: boolean) => Promise<SourceDataConfigurationSingleResponse | undefined>
    trial?: ITrial;
    metadata?: ITrialFormMetadata;
    loading?: boolean;
    updating?: boolean;
    skeletonCount?: number;
}

const ConfigurationForm: FunctionComponent<IConfigurationForm> = ({
    isTrialConfiguration,
    configuration,
    updateConfiguration,
    trialConfiguration,
    metadata,
    trial,
    loading,
    updating,
    skeletonCount
}) => {
    const formsFeature = useContext(FormsFeatureContext);
    const navigate = useNavigate();
    const { trialCode } = useParams();

    const initialValue: FormType = useMemo(() => {
        return {
            configuration,
            trialConfiguration
        }
    }, [configuration, trialConfiguration])

    const loadingArray = useMemo(() => {
        return Array(skeletonCount ?? 20).fill(0);
    }, [skeletonCount]);

    const [tab, setTab] = useState(0);

    const editContext = useMemo(() => {
        return new Observable<IConfigurationFormEditContext | undefined>(undefined);
    }, [])

    const handleChange = (event: any, newValue: any) => {
        setTab(newValue);
    };
    
    const onSubmit: IFormSubmit<FormType, IValidationError> = useCallback(async ( state ) => {
        if (!state.value) {
            return {}
        }   

        const response = await formsFeature.serviceStackClient.post(new PostSaveSourceDataConfiguration({ data: state.value.configuration }))

        navigate(`/${trialCode}/configurations`, { state: { success: true }});

        // if (!!response.data?.id && response.data?.id !== state.value.configuration?.id && !isTrialConfiguration) {
        //     navigate(`/${trialCode}/configurations/${response.data.id}`);
        // }
        // else if (updateConfiguration) {
        //     await updateConfiguration(response);
        // }

        return {}
    }, [formsFeature.serviceStackClient, navigate, trialCode, isTrialConfiguration, updateConfiguration]);

    const formProcessor = useMemo(() => {
        return new ConfigurationFormProcessor();
    }, []);

    const [processed, setProcessed] = useState<IConfigurationData>({ events: [], forms: [] })

    useEffect(() => {
        const process = async () => {
            const events = await formProcessor.processConfiguration(configuration, trialConfiguration, metadata, isTrialConfiguration);
            setProcessed(events);
        }

        process();
    }, [formProcessor, configuration, trialConfiguration, metadata, setProcessed, isTrialConfiguration]);

    return (
        <Form
            onSubmit={onSubmit}
            initialValue={initialValue}
            debug={import.meta.env.DEV}
            globalErrorPropertyPath="submit"
        >
            <ConfigurationFormEditContext.Provider value={editContext}>
                {
                    !isTrialConfiguration && (
                        <Grid 
                            container
                            spacing={2}
                        >
                            <Grid 
                                item
                                xs={12}
                                sm={12}
                                md={6}
                                lg={8}
                                xl={9}
                            >
                                <ConfigurationDetails>
                                    {
                                        !!loading && (
                                            <>
                                                <InputSkeleton variant="rectangular" width="100%" height={56} />
                                                <InputSkeleton variant="rectangular" width="100%" height={56} />
                                            </>
                                        )
                                    }
                                    {
                                        !loading && (
                                            <>
                                                <InputOnlyField 
                                                    label="Name"
                                                    propertyPath="configuration.name"
                                                    component={Text}
                                                    margin="normal"
                                                />
                                                <InputOnlyField 
                                                    label="Description"
                                                    propertyPath="configuration.description"
                                                    component={Text}
                                                    multiline
                                                    margin="normal"
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationDetails>
                            </Grid>
                        </Grid>
                    )
                }
                <Grid 
                    container
                    spacing={2}
                >
                    <Grid 
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={8}
                        xl={9}
                    >
                        <Paper>
                            <Box>
                                <Tabs value={tab} onChange={handleChange}>
                                    <Tab 
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                        mr: 2
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={faCalendarDays} />
                                                </Box>
                                                Events
                                            </Box>
                                        } 
                                    />
                                    <Tab 
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                        mr: 2
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={faSquareList} />
                                                </Box>
                                                Forms
                                            </Box>
                                        } 
                                    />
                                </Tabs>
                                <Divider />
                            </Box>
                            {
                                updating && (
                                    <Box
                                        sx={{

                                            position: 'relative'
                                        }}
                                    >
                                        <LinearProgress 
                                            color="secondary"
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0
                                            }}
                                        />
                                    </Box>
                                )
                            }
                                <List disablePadding>
                                    {
                                        loading && loadingArray?.map((x, i, a) => {
                                            return (
                                                <Fragment key={i}>
                                                    <ConfigurationFormEventSkeleton />
                                                    { i < (a.length - 1) && <Divider />} 
                                                </Fragment>
                                            );
                                        })
                                    }
                                    {
                                        !loading && (
                                            <>
                                                <DataComponent 
                                                    tab={tab}
                                                    processed={processed}
                                                />
                                            </>
                                        )
                                    }
                                </List>
                        </Paper>
                    </Grid>
                    <GridSearchColumn 
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={4}
                        xl={3}
                    >
                        <ConfigurationFormEdit loading={loading} trial={trial} metadata={metadata} />
                    </GridSearchColumn>
                </Grid>
            </ConfigurationFormEditContext.Provider>
        </Form>
    );
}

interface IDataComponent {
    tab: number;
    processed: IConfigurationData;
}

const DataComponent: FC<IDataComponent> = ({ 
    tab,
    processed
}) => {    
    const [expanded, setExpanding] = useState<Record<string, boolean>>({});

    const context = useMemo(() => {
        return {
            setExpanded: (identifier: string, value: boolean, collapseChildren?: boolean) => {
                setExpanding(c => {
                    const newExpanded = {...c}

                    if (collapseChildren && !value) {
                        for (let key of Object.keys(c).filter(x => x.startsWith(identifier))) {
                            newExpanded[key] = value;
                        }
                    }
                    else {
                        newExpanded[identifier] = value;
                    }

                    return newExpanded;
                });
            },
            toggleExpanding: (identifier: string, collapseChildren?: boolean) => {
                setExpanding(c => {
                    const newExpanded = {...c}

                    if (collapseChildren && c[identifier]) {
                        for (let key of Object.keys(c).filter(x => x.startsWith(identifier))) {
                            newExpanded[key] = !c[key];
                        }
                    }
                    else {
                        newExpanded[identifier] = !c[identifier];
                    }

                    return newExpanded;
                });
            },
            expanded: expanded
        }
    }, [expanded, setExpanding]);

    const processedList = useMemo(() => {
        if (tab === 1) {
            return  processed.forms
                .filter((x) => {
                    switch (x.type) {
                        case ConfigurationType.Version:
                            return expanded[x.formIdentifier!];
                        case ConfigurationType.Section:
                            return expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!];
                        case ConfigurationType.Field:
                            return expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!] && (!x.formSectionIdentifier || expanded[x.formSectionIdentifier!]);
                        case ConfigurationType.Value:
                            return expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!] && (!x.formSectionIdentifier || expanded[x.formSectionIdentifier!]) && expanded[x.formFieldIdentifier!];
                    }

                    return true;
                });
        }

        return processed.events
            .filter((x) => {
                switch (x.type) {
                    case ConfigurationType.Form:
                        return expanded[x.eventIdentifier!];
                    case ConfigurationType.Version:
                        return expanded[x.eventIdentifier!] && expanded[x.formIdentifier!];
                    case ConfigurationType.Section:
                        return expanded[x.eventIdentifier!] && expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!];
                    case ConfigurationType.Field:
                        return expanded[x.eventIdentifier!] && expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!] && (!x.formSectionIdentifier || expanded[x.formSectionIdentifier!]);
                    case ConfigurationType.Value:
                        return expanded[x.eventIdentifier!] && expanded[x.formIdentifier!] && expanded[x.formVersionIdentifier!] && (!x.formSectionIdentifier || expanded[x.formSectionIdentifier!]) && expanded[x.formFieldIdentifier!];
                }

                return true;
            })
    }, [processed, expanded, tab])


    return (
        <Virtuoso
            context={context}
            useWindowScroll
            computeItemKey={(index, item) => {
                const d = item as any;

                return d.formFieldValueIdentifier ?? 
                    d.formFieldIdentifier ??
                    d.formSectionIdentifier ??
                    d.formVersionIdentifier ??
                    d.formIdentifier ??
                    d.eventIdentifier
            }}
            data={processedList}
            itemContent={virtuosoItemContent}
        />
    )
}

export default ConfigurationForm;